import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  DataTable,
  cn,
  createDataTableColumnHelper,
  type DataTableColumnDef,
  type DataTableProps,
} from "@nasam/ui";
import { paginateLocally } from "@nasam/kernel";

import { sortBulkRows } from "./rows";
import type { BulkRowBase, BulkStatus } from "./types";

const PAGE_SIZE = 10;

/** The colours of each status, on its badge and its filter chip. */
export const BULK_STATUS_STYLES: Record<BulkStatus, string> = {
  VALID: "border-badge-border-green bg-badge-bg-light-green text-badge-text-green",
  INVALID: "border-badge-border-red bg-badge-bg-light-red text-badge-text-red",
  DUPLICATE:
    "border-badge-border-yellow bg-badge-bg-light-yellow text-badge-text-yellow",
};

function BulkStatusBadge({
  copyKey,
  status,
}: {
  readonly copyKey: string;
  readonly status: BulkStatus;
}) {
  const { t } = useTranslation("fleetMonitoring", { keyPrefix: copyKey });
  return (
    <span
      className={cn(
        "inline-flex h-7 w-full min-w-26 items-center justify-center rounded-md px-3 text-body-s-b",
        BULK_STATUS_STYLES[status],
      )}
    >
      {t(`status.${status}`)}
    </span>
  );
}

/** A plain cell of the table. */
export function bulkCellText(value: string) {
  return <span className="block truncate text-body-s-r text-text">{value}</span>;
}

export interface BulkRowsTableProps<Row extends BulkRowBase> {
  /** The feature's copy under `fleetMonitoring`, e.g. `pois.bulkUpload`. */
  readonly copyKey: string;
  readonly rows: readonly Row[];
  /** The columns of the sheet itself, between Row and Status. Keep them stable
   *  (`useMemo`). */
  readonly sheetColumns: ReadonlyArray<DataTableColumnDef<Row, string>>;
  /** The Check step says why a row can't be imported; Preview has only
   *  valid rows, so no reasons. */
  readonly showReason: boolean;
}

/**
 * The rows of an uploaded sheet as the API checked them, a page at a time
 * (they all come at once, up to 5,000). Status can be sorted. Remount it
 * (`key`) to start over on page 1 with other rows.
 */
export function BulkRowsTable<Row extends BulkRowBase>({
  copyKey,
  rows,
  sheetColumns,
  showReason,
}: BulkRowsTableProps<Row>) {
  type TableProps = DataTableProps<Row, string>;
  type Sorting = NonNullable<TableProps["sorting"]>;

  const { t } = useTranslation("fleetMonitoring", {
    keyPrefix: `${copyKey}.table`,
  });
  const [sorting, setSorting] = useState<Sorting>([]);
  const [page, setPage] = useState({ pageIndex: 0, pageSize: PAGE_SIZE });

  const sort = sorting[0];
  const sorted = useMemo(
    () =>
      sort === undefined ? rows : sortBulkRows(rows, sort.desc ? "desc" : "asc"),
    [rows, sort],
  );
  const shown = paginateLocally(sorted, page.pageIndex + 1, page.pageSize);

  const columns = useMemo<ReadonlyArray<DataTableColumnDef<Row, string>>>(() => {
    const column = createDataTableColumnHelper<Row>();
    return [
      column.accessor((row) => String(row.row), {
        id: "row",
        header: t("row"),
        cell: (info) => bulkCellText(info.getValue()),
        enableSorting: false,
      }),
      ...sheetColumns,
      column.accessor((row): string => row.status, {
        id: "status",
        header: t("status"),
        cell: (info) => (
          <BulkStatusBadge
            copyKey={copyKey}
            status={info.row.original.status}
          />
        ),
        enableSorting: true,
      }),
      ...(showReason
        ? [
            column.accessor((row) => row.errors.join(" "), {
              id: "reason",
              header: t("reason"),
              cell: (info) => (
                <ul className="flex min-w-48 flex-col gap-0.5 text-body-caption-m text-badge-text-red">
                  {info.row.original.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              ),
              enableSorting: false,
            }),
          ]
        : []),
    ];
  }, [copyKey, sheetColumns, showReason, t]);

  const onSortingChange: TableProps["onSortingChange"] = (updater) => {
    setSorting((current) =>
      typeof updater === "function" ? updater(current) : updater,
    );
    setPage((current) => ({ ...current, pageIndex: 0 }));
  };
  const onPaginationChange: TableProps["onPaginationChange"] = (updater) =>
    setPage((current) => {
      const next = typeof updater === "function" ? updater(current) : updater;
      // A new page size starts over from the first page.
      return next.pageSize === current.pageSize
        ? next
        : { pageIndex: 0, pageSize: next.pageSize };
    });

  return (
    <DataTable
      data={shown.data}
      columns={columns}
      paginationMeta={shown.meta}
      // The rows scroll inside the table, not the dialog, and the pagination
      // stays in view: 400px on desktop, on a phone what the dialog leaves.
      containerClassName="flex min-h-40 flex-auto flex-col md:h-100 md:flex-initial"
      sorting={sorting}
      onSortingChange={onSortingChange}
      pageIndex={shown.meta.currentPage - 1}
      pageSize={page.pageSize}
      onPaginationChange={onPaginationChange}
      labels={{
        empty: t("empty"),
        error: t("error"),
        loading: t("loading"),
        showingCount: (_count, total) => t("total", { total }),
        rowsPerPageLabel: t("rowsPerPageLabel"),
        nextPage: t("nextPage"),
        previousPage: t("previousPage"),
        rowsPerPage: t("pagination"),
        sortAscending: t("sortAscending"),
        sortDescending: t("sortDescending"),
        // Required by the type; this table has no action column.
        actionMenuTrigger: t("actions"),
      }}
    />
  );
}
